import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseIcon, CameraIcon, CodeIcon, FlagIcon, MegaphoneIcon, SearchIcon, UsersIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CtaBand } from '../components/ui/CtaBand';
const roles = [{
  Icon: UsersIcon,
  title: 'Ward volunteer',
  description: 'Door-to-door canvassing, voter education and polling unit support in your ward.',
  commitment: '2–6 hours a week · in person'
}, {
  Icon: MegaphoneIcon,
  title: 'Digital campaign',
  description: 'Share verified messaging, counter misinformation and grow local groups online.',
  commitment: 'Flexible · fully remote'
}, {
  Icon: FlagIcon,
  title: 'Community mobilisation',
  description: 'Organise meetings with unions, market associations, faith and youth groups.',
  commitment: '4–10 hours a week · in person'
}, {
  Icon: CameraIcon,
  title: 'Media & creative',
  description: 'Photography, video, design and translation for campaign materials.',
  commitment: 'Project based · remote or in person'
}, {
  Icon: SearchIcon,
  title: 'Research & policy',
  description: 'Support the policy directorate with data, sourcing and fact verification.',
  commitment: '4 hours a week · remote'
}, {
  Icon: CodeIcon,
  title: 'Technology',
  description: 'Build and maintain the tools the campaign runs on, from data to logistics.',
  commitment: 'Flexible · remote'
}, {
  Icon: BriefcaseIcon,
  title: 'Professional services',
  description: 'Legal, medical, accounting and security professionals supporting the campaign.',
  commitment: 'On call · as needed'
}, {
  Icon: FlagIcon,
  title: 'Ward or LGA coordinator',
  description: 'Lead a team, run training and report weekly to the state campaign office.',
  commitment: '10+ hours a week · in person'
}];
export function Join() {
  return <>
      <PageHero eyebrow="Get involved" title="There is a place for you in this movement." intro="412,000 Nigerians have already registered across all 774 local government areas. Here is where you would fit." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Join'
    }]} actions={<Link to="/join/apply" className="inline-flex h-[52px] items-center gap-2 rounded-lg bg-green-600 px-7 text-base font-semibold text-white transition-colors duration-150 ease-emphasis hover:bg-green-700" data-id="element-990" data-mp="1zoci91ulpc2w">
            Start your application
            <ArrowRightIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-991" data-mp="24hf3mmp85f80" />
          </Link>} data-id="element-989" data-mp="1bsgmnswu8j4a" />

      <section className="bg-sand-50 py-16 lg:py-24" aria-labelledby="roles-heading" data-id="element-992" data-mp="2ih6on7e5rvvi">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-993" data-mp="2dhtstx5b7egs">
          <SectionHeading eyebrow="Ways to participate" title="Eight ways to help" intro="Every role has a named coordinator, a training session and a clear expectation of time." data-id="element-994" data-mp="2x2u9kus6r4ff" />
          <ul className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-id="element-995" data-mp="2n5ft4v69ifn4">
            {roles.map((role, index) => <li key={role.title} data-id="element-996" data-mp="2s2g0d9euloft" data-mpi={index}>
                <Reveal delay={Math.min(index, 7) * 0.03} className="h-full" data-id="element-997" data-mp="2s5kd4v6dpsi3">
                  <Link to="/join/apply" className="group flex h-full flex-col rounded-card border border-sand-200 bg-white p-6 transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card" data-id="element-998" data-mp="2eb1gz1h13l88">
                    <role.Icon aria-hidden className="h-6 w-6 text-green-600" data-id="element-999" data-mp="29drmv8ip2le2" />
                    <span className="mt-4 font-display text-[19px] leading-snug text-ink" data-id="element-1000" data-mp="1vervht3srse7">
                      {role.title}
                    </span>
                    <span className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-1001" data-mp="1sbvsf7mblexd">
                      {role.description}
                    </span>
                    <span className="mt-auto block pt-5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500" data-id="element-1002" data-mp="1ndasl9azcx9g">
                      {role.commitment}
                    </span>
                  </Link>
                </Reveal>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="process-heading" data-id="element-1003" data-mp="19mp4952rbv46">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1004" data-mp="14kq4s6a51ici">
          <SectionHeading eyebrow="What happens next" title="From application to first assignment" data-id="element-1005" data-mp="zmevpxro3bvc" />
          <ol className="mt-10 grid gap-6 md:grid-cols-4" data-id="element-1006" data-mp="up6kyi8uma44">
            {[['Apply', 'Five short steps. Around three minutes.'], ['Verification', 'We confirm your phone number and polling unit.'], ['Coordinator contact', 'Your ward coordinator calls within five working days.'], ['Training', 'A two-hour session, in person or online, before your first shift.']].map(([title, detail], index) => <li key={title} className="border-t-2 border-green-600 pt-5" data-id="element-1007" data-mp="36bgwgryfwdd9" data-mpi={index}>
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-green-700" data-id="element-1008" data-mp="36djr5pajeuob">
                  Step {index + 1}
                </p>
                <h3 className="mt-2 font-display text-[20px] text-ink" data-id="element-1009" data-mp="2elccgueldsck">{title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-1010" data-mp="2ilcrag5ls5k5">{detail}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <CtaBand title="Ready when you are." description="The application takes about three minutes, and you can change your role at any time." primary={{
      label: 'Start your application',
      to: '/join/apply'
    }} secondary={{
      label: 'Download the volunteer guide',
      to: '/resources'
    }} data-id="element-1011" data-mp="2nnbu9oxfl0gk" />
    </>;
}